/**
 * SplitPane — horizontally-split resizable pane.
 *
 * Reads splitPercent from the editor store (0–100).
 * The user can drag the handle to resize; the toolbar view-mode
 * buttons set preset values (0 / 50 / 100).
 *
 * When splitPercent is 0 or 100 the hidden panel is unmounted and
 * the divider collapses to zero width.
 */

import { memo, useCallback, useRef, useState } from "react";
import { useT } from "@magic-md/i18n/react";
import { useEditorStore } from "../store/editor-store";
import { usePaneChromeContextOptional } from "./PaneContext";

export const SplitPane = memo(function SplitPane({
    left,
    right,
}: {
    left: React.ReactNode;
    right: React.ReactNode;
}) {
    const t = useT("editor");
    const paneCtx = usePaneChromeContextOptional();
    const globalSplitPercent = useEditorStore((s) => s.splitPercent);
    const globalSetSplitPercent = useEditorStore((s) => s.setSplitPercent);
    const setPaneSplitPercent = useEditorStore((s) => s.setPaneSplitPercent);

    // Use pane-scoped values if within a PaneProvider, otherwise global
    const splitPercent = paneCtx?.splitPercent ?? globalSplitPercent;
    const setSplitPercent = useCallback(
        (pct: number) => {
            if (paneCtx) {
                setPaneSplitPercent(paneCtx.paneId, pct);
            } else {
                globalSetSplitPercent(pct);
            }
        },
        [paneCtx, setPaneSplitPercent, globalSetSplitPercent],
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const onMouseDown = useCallback(() => {
        setIsDragging(true);

        const onMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const pct = ((e.clientX - rect.left) / rect.width) * 100;
            // Snap to 0/100 near edges, otherwise clamp to [15, 85]
            const snapped = pct < 8 ? 0 : pct > 92 ? 100 : Math.max(15, Math.min(85, pct));
            setSplitPercent(snapped);
        };

        const onMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, [setSplitPercent]);

    const showLeft = splitPercent > 0;
    const showRight = splitPercent < 100;
    const showHandle = showLeft && showRight;

    return (
        <div ref={containerRef} className="relative flex flex-1 min-h-0 min-w-0">
            {/* Drag overlay — prevents editor / preview from capturing pointer events */}
            {isDragging && (
                <div className="absolute inset-0 z-50" style={{ cursor: "col-resize" }} />
            )}

            {showLeft && (
                <div
                    style={{ width: showRight ? `${splitPercent}%` : "100%" }}
                    className="min-w-0 min-h-0"
                >
                    {left}
                </div>
            )}

            {showHandle && (
                <div
                    role="separator"
                    className="resize-handle"
                    aria-orientation="vertical"
                    aria-label={t("pane.resizeEditorPreview")}
                    aria-valuenow={splitPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    tabIndex={0}
                    onMouseDown={onMouseDown}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowLeft")
                            setSplitPercent(Math.max(0, splitPercent - 2));
                        if (e.key === "ArrowRight")
                            setSplitPercent(Math.min(100, splitPercent + 2));
                    }}
                />
            )}

            {showRight && (
                <div
                    style={{ width: showLeft ? `${100 - splitPercent}%` : "100%" }}
                    className="min-w-0 min-h-0"
                >
                    {right}
                </div>
            )}
        </div>
    );
});
